<script lang="ts">
import { defineBlock } from '@bagelink/blox'
import { defineSchema, $ } from '@bagelink/vue'
const defaults = {
	columns: [
		{
			title: 'General',
			links: [
				{ label: 'About Us', link: '/about' },
				{ label: 'Contact Us', link: '/contact' },
				{ label: 'עברית', link: 'https://www.thetorah.co.il/' },
				{ label: 'RSS', link: '/rss.xml' },
				{ label: 'Terms of Use', link: '/terms' },
			],
		},
		{
			title: 'Torah Portion',
			links: [
				{ label: 'Genesis', link: '/book/genesis' },
				{ label: 'Exodus', link: '/book/exodus' },
				{ label: 'Leviticus', link: '/book/leviticus' },
				{ label: 'Numbers', link: '/book/numbers' },
				{ label: 'Deuteronomy', link: '/book/deuteronomy' },
			],
		},
		{
			title: 'Holidays',
			links: [
				{ label: 'Rosh Hashanah', link: '/holidays/rosh-hashanah' },
				{ label: 'Yom Kippur', link: '/holidays/yom-kippur' },
				{ label: 'Sukkot', link: '/holidays/sukkot' },
				{ label: 'Passover', link: '/holidays/passover' },
				{ label: 'Shavuot', link: '/holidays/shavuot' },
				{ label: 'Chanukah', link: '/holidays/chanukah' },
				{ label: 'Purim', link: '/holidays/purim' },
			],
		},
		{
			title: 'Scholarship and Faith',
			links: [
				{ label: 'Biblical Criticism', link: '/categories/bible-criticism' },
				{ label: 'Modern Faith', link: '/categories/modern-faith' },
				{ label: 'Morality and Ethics', link: '/categories/morality-and-ethics' },
				{ label: 'Archaeology', link: '/categories/archaeology' },
				{ label: 'Medieval Interpretation', link: '/categories/medieval-interpretation' },
			],
		},
	],
	newsletterTitle: 'Subscribe to our newsletter',
	socialLinks: [
		{ icon: 'facebook-f', link: '#' },
		{ icon: 'x-twitter', link: '#' },
	],
	copyright:
		'Launched Shavuot 5773 / 2013 | Copyright © {year} Academic Torah Institute, All Rights Reserved',
	donateLink: '/donate',
	logoWhite: '',
}
const schema = defineSchema({
	columns: $.array(
		'Columns',
		defineSchema({
			title: $.text('Title'),
			links: $.array(
				'Links',
				defineSchema({
					label: $.text('Label'),
					link: $.text('Link'),
				}),
			{ collapsible: true },),
		}),
	{ collapsible: true },),
	newsletterTitle: $.text('Newsletter Title'),
	socialLinks: $.array(
		'Social Links',
		defineSchema({
			icon: $.text('Icon'),
			link: $.text('Link'),
		}),
	{ collapsible: true },),
	copyright: $.text('Copyright'),
	donateLink: $.text('Donate Link'),
	logoWhite: $.upload('White Logo'),
})
export const BlockFooter = defineBlock({
	thumbnail: '/block-thumbs/BlockFooter.svg',
	category: 'Layout',
	order: 230,
	name: 'BlockFooter',
	label: 'Footer',
	icon: 'bottom_navigation',
	global: true,
	schema,
	defaults,
})
</script>
<script setup lang="ts">
import { useGlobalBlock } from '@bagelink/blox'
import { Btn, Icon } from '@bagelink/vue'
import { computed, ref } from 'vue'
import logoWhiteAsset from '@/assets/logo-thetorah-white.svg'
import { useNewsletter } from '@/composables/useNewsletter'

// The footer "Subscribe to our newsletter" row opens the same shared popup as
// the nav's "Newsletter" link (rendered once by BlockNav via NewsletterDialog).
const { openNewsletter } = useNewsletter()




interface FooterLink {
	label: string
	link: string
}

interface FooterColumn {
	title: string
	links: FooterLink[]
}

interface SocialLink {
	icon: string
	link: string
}

const props = defineProps<{
	columns?: FooterColumn[]
	newsletterTitle?: string
	socialLinks?: SocialLink[]
	copyright?: string
	donateLink?: string
	logoWhite?: string
}>()

// Global block: content is shared site-wide and edited once in the studio.
// Prefer the CMS-stored content, then props, then schema defaults.
const { data: remote } = useGlobalBlock<{
	columns?: FooterColumn[]
	newsletterTitle?: string
	socialLinks?: SocialLink[]
	copyright?: string
	donateLink?: string
	logoWhite?: string
}>('BlockFooter')
const columns = computed<FooterColumn[]>(() => {
	const normalized = (remote.value?.columns?.length
		? remote.value.columns
		: props.columns?.length
			? props.columns
			: defaults.columns)
		.map(column => ({
			...column,
			links: column.links.map(link =>
				link.label.trim().toLowerCase() === 'rss' ? { ...link, link: '/rss.xml' } : link,
			),
		}))
	if (!normalized.some(column => column.links.some(link => link.label.trim().toLowerCase() === 'rss'))) {
		normalized[0]?.links.push({ label: 'RSS', link: '/rss.xml' })
	}
	return normalized
})
const newsletterTitle = computed(
	() => remote.value?.newsletterTitle ?? props.newsletterTitle ?? defaults.newsletterTitle,
)
const socialLinks = computed<SocialLink[]>(() =>
	remote.value?.socialLinks?.length
		? remote.value.socialLinks
		: props.socialLinks?.length
			? props.socialLinks
			: defaults.socialLinks,
)
const copyright = computed(() => remote.value?.copyright ?? props.copyright ?? defaults.copyright)
const donateLink = computed(
	() => remote.value?.donateLink ?? props.donateLink ?? defaults.donateLink,
)
const logoWhite = computed(() => remote.value?.logoWhite ?? props.logoWhite ?? defaults.logoWhite)

const currentYear = new Date().getFullYear()

// Toggle states for mobile collapsible sections
const toggleStates = ref<Record<number, boolean>>({})

function toggleSection(index: number) {
	toggleStates.value[index] = !toggleStates.value[index]
}

function renderCopyright(text?: string) {
	return (text ?? '').replace('{year}', String(currentYear))
}
</script>

<template>
	<footer class="bg-black color-white py-2 m_px-2 m_py-1 footer">
		<div class="w1100px">
			<div class="grid grid-wrap-4 gap-1 pb-1 pt-2 m_pt-1 m_txt-center">
				<div
					v-for="(col, index) in columns"
					:key="index"
					class="m_border-bottom m_pb-05 footerDrop"
				>
					<h3
						class="txt14 m_txt14 m-0 ps-075 pb-05 m_flex m_gap-05 m_align-items-center m_justify-content-center m_pointer"
						@click="toggleSection(index)"
					>
						{{ col.title }}
						<Icon
							name="keyboard_arrow_down"
							class="line-height-0 none m_block"
							:class="{ 'm_rotate-180': toggleStates[index] }"
						/>
					</h3>
					<div
						class="transition-400 popFooterDrop"
						:class="{ openPopFooterDrop: toggleStates[index] }"
					>
						<div class="flex column gap-05 align-items-start m_align-items-center">
							<Btn
								v-for="link in col.links"
								:key="link.link"
								flat
								thin
								class="light txt13 m_txt12"
								color="white"
								:to="/^https?:\/\//.test(link.link) || link.link === '/rss.xml' ? undefined : link.link"
								:href="/^https?:\/\//.test(link.link) || link.link === '/rss.xml' ? link.link : undefined"
								:value="link.label"
							/>
						</div>
					</div>
				</div>
			</div>

			<div>
				<div class="flex gap-1 space-between m_column-reverse m_pt-075">
					<Btn :to="donateLink" flat color="white" outline value="Donate" class="px-1" />
					<button
						type="button"
						class="display-flex border txt12 hover pointer bg-transparent color-inherit p-0"
						style="height: var(--bgl-btn-height)"
						@click="openNewsletter"
					>
						<span
							class="bg-white h-100p1 color-gray-130 vertical-middle px-1 flex justify-content-center"
						>
							{{ newsletterTitle }}
						</span>
						<span class="aspect-ratio-1 flex justify-content-center">
							<img src="@/assets/send.svg" alt="" />
						</span>
					</button>
				</div>
				<div class="flex gap-1 m_block m_txt-center border-top pt-1 mt-1 m_pt-1-5 m_mt-1-5">
					<RouterLink to="/" class="me-auto">
						<img :src="logoWhite || logoWhiteAsset" alt="thetorah.com" />
					</RouterLink>
					<p class="txt11 m_py-2">
						{{ renderCopyright(copyright) }}
					</p>
					<Btn
						v-for="social in socialLinks"
						:key="social.icon"
						flat
						thin
						color="white"
						class="m_mx-05"
						:icon="social.icon"
						:href="social.link"
					/>
				</div>
			</div>
		</div>
	</footer>
</template>

<style scoped>
footer {
	--bgl-border-color: var(--bgl-gray-70);
}

/* Mobile toggle functionality */
@media (max-width: 910px) {
	.m_rotate-180 {
		transform: rotate(180deg);
	}
	.popFooterDrop {
		max-height: 0;
		overflow: hidden;
	}
	.openPopFooterDrop {
		max-height: 1000px;
	}
}
</style>
